import React from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Message from 'components/parts/message';
import clientConfig from 'clientConfig';
import { Button } from 'react-bootstrap';
import { consoleLogger } from 'helpers';
import { Auth } from 'aws-amplify';
import { getProgramFiles } from 'actions/programFileAction';
import { setBreadcrumb } from 'actions/breadcrumbAction';
import { clearMessage, setMessage } from 'actions/messageAction';
import { setLoading, clearLoading } from 'actions/loadingAction';
import { convertTimestampToDate, nl2br } from 'helpers';
import { lang } from 'lang';
import { openStatus } from 'const';
import ItemDetail from 'components/parts/item/itemDetail';
import UnitLoading from 'components/parts/loading/unitLoading';

/**
 * バージョン詳細画面
 */
const SrvVersionDetail = (props) => {
  const dispatch = useDispatch();
  const { msg, error } = lang;
  const { id } = props.match.params;
  const [versionState, setVersion ] = React.useState({});
  const [buttonStatus, setButtonStatus] = React.useState({delete: false, open_flg: false});
  // リクエストキャンセルトークンを取得
  const cancelToken = axios.CancelToken.source();
  // グローバルステートから対象のプログラムファイルを取り出す
  const programFileState = useSelector(
    (state) => state.programFile.items.filter((item) => {
      if (item.key === id) {
        return true;
      } else if (item.key === id + '_2') {
        return true;
      } else {
        return false;
      }
    })
  );
  const fileList = {};
  for (const file of programFileState) {
    fileList[file.key] = file;
  }
  const header = useSelector((state) => state.header);
  React.useEffect(() => {
    // メッセージの初期化
    dispatch(clearMessage());
    // パンくずリストにバージョンを設定
    dispatch(setBreadcrumb(id));
    // プログラムファイル・バージョン情報を取得する
    getData(cancelToken);
    return () => {
      // unmounted component時にコールされる
      cancelToken.cancel('unmounted component.');
    };
  }, [header.mail]);

  // プログラムファイル・バージョン情報を取得する
  const getData = cancelToken => {
    Auth.currentSession().then(async currentSession => {
      // ローディング開始
      dispatch(setLoading('unit'));
      // 削除ボタンを非活性化
      setButtonStatus({...buttonStatus, delete: true});
      await dispatch(
        await getProgramFiles()
      ).then(async (result) => {
        const file = result.items.filter((item) => item.key === id);
        // Success
        if (result.status === 200 && file.length !== 0) {
          // バージョンを取得する
          const url = clientConfig['api_url'] + '/srv/versions/' + id;
          const requestData = {
            method: 'get',
            url: url,
            headers: { 'Authorization': currentSession.idToken.jwtToken },
            cancelToken: cancelToken.token
          };
          await axios(requestData)
            .then(res => {
              consoleLogger('API GET:' + url);
              consoleLogger(res);
              // Success
              if (res.status === 200) {
                setVersion({
                  ...res.data.Item.version,
                  open_flg: res.data.Item.version.open_flg === '0' ? false : true
                });
              // Error
              } else {
                dispatch(setMessage('danger', error.version.get + `\n${res.data.errcode}`));
              }
            }).catch(err => {
              consoleLogger(err);
              if (axios.isCancel(err)) {
                return false;
              }
              if (err.response === undefined) {
                dispatch(setMessage('danger', error.version.get + `\n${err.message}`));
              } else {
                // Not Found
                if (err.response.status === 404 && err.response.data.errcode === 'ITEM_NOT_FOUND') {
                  dispatch(setMessage('info', msg.version.not_exist));
                // Error
                } else {
                  dispatch(setMessage('danger', error.version.get + `\n${err.response.data.errcode}`));
                }
              }
            });
        // Error
        } else {
          dispatch(setMessage('info', msg.update_file.not_exist));
        }
      }).catch((err) => {
        consoleLogger(err);
      });
      // 削除ボタンを活性化
      setButtonStatus({...buttonStatus, delete: false});
      // ローディングクリア
      dispatch(clearLoading());
    }).catch((err) => {
      consoleLogger(err);
      dispatch(setMessage('danger', error.version.get));
    });
  };

  // プログラムファイルがステートに1件もない場合
  if (programFileState.length === 0) {
    return <div className="col-10 pt-3 pb-3"><UnitLoading/><Message/></div>;
  // プログラムファイル2のアクセスの場合
  } else if (id.lastIndexOf('_2') === id.length - 2) {
    return <div className="col-10 pt-3 pb-3"><UnitLoading/><Message/></div>;
  } else {
    /**
     * 削除ボタンが押下されたときに実行するメソッド
     */
    const clickDeleteButton = async full_filename => {
      const currentSession = await Auth.currentSession();
      const url = clientConfig['api_url'] + '/srv/versions/' + full_filename;
      const requestData = {
        method: 'delete',
        url: url,
        headers: { 'Authorization': currentSession.idToken.jwtToken },
      };
      // メッセージの初期化
      dispatch(clearMessage());

      // 削除ボタンを非活性化
      setButtonStatus({open_flg: true, delete: true});

      await axios(requestData)
        .then(async res => {
          consoleLogger('API DELETE:' + url);
          consoleLogger(res);
          // Success
          if (res.status === 200) {
            props.history.push({
              pathname: '/srv/files',
              state: { 
                keepMessage: true,  // 一覧画面表示時にメッセージを残す
                message: {
                  type: 'success',
                  msg: msg.update_file.remove.replace('%s', full_filename)
                }
              }
            });            
          // Error
          } else {
            dispatch(setMessage('danger', error.version.remove + `\n${res.data.errcode}`));
          }
        }).catch(err => {
          consoleLogger(err.response);
          if (err.response === undefined) {
            dispatch(setMessage('danger', error.version.remove + `\n${err.message}`));
          } else {
            dispatch(setMessage('danger', error.version.remove + `\n${err.response.data.errcode}`));
          }
        });
      // 削除ボタンを活性化
      setButtonStatus({open_flg: false, delete: false});
    };
    /**
    * 公開/非公開ボタンが押下されたときに実行するメソッド
    */
    const toggleButton = async (full_filename, open_flg) => {
      const currentSession = await Auth.currentSession();
      const url = clientConfig['api_url'] + '/srv/versions/' + full_filename + '/openflg';
      const requestData = {
        method: 'put',
        url: url,
        headers: { 'Authorization': currentSession.idToken.jwtToken },
        params: {
          open_flg: open_flg ? '1' : '0'
        }
      };
      // メッセージの初期化
      dispatch(clearMessage());

      // 削除、公開/非公開ボタンを非活性化
      setButtonStatus({delete: true, open_flg: true});

      await axios(requestData)
        .then(async res => {
          consoleLogger('API PUT:' + url);
          consoleLogger(res);
          // Success
          if (res.status === 200) {
            setVersion({...versionState, open_flg: open_flg});
            if (open_flg) {
              dispatch(setMessage('success', msg.version.publish));
            } else {
              dispatch(setMessage('success', msg.version.unpublish));
            }
          // Errord
          } else {
            if (open_flg) {
              dispatch(setMessage('danger', error.version.publish  + `\n${res.data.errcode}`));
            } else {
              dispatch(setMessage('danger', error.version.unpublish  + `\n${res.data.errcode}`));
            }
          }
        }).catch(err => {
          consoleLogger(err.response);
          const message = open_flg ? error.version.publish : error.version.unpublish;
          if (err.response === undefined) {
            dispatch(setMessage('danger', message + `\n${err.message}`));
          } else {
            dispatch(setMessage('danger', message  + `\n${err.response.data.errcode}`));
          }
        });

      // 削除・公開/非公開ボタンを活性化
      setButtonStatus({delete: false, open_flg: false});
    };

    let showRemoveButton = false;
    // バージョンがない場合
    if (Object.keys(versionState).length === 0) {
      showRemoveButton = true;
    // バージョンが存在する場合
    } else {
      // 公開の場合はRemoveボタン非表示／そうでない場合は表示
      showRemoveButton = versionState.open_flg ? false : true;
    }
    return (
      <>
        <div className="col-10 pt-3 pb-3">
          <UnitLoading/>
          <Div>      
            {showRemoveButton && <DeleteButton variant="danger" disabled={buttonStatus.delete} onClick={() => clickDeleteButton(id)}>
            Remove
            </DeleteButton>}
            {versionState.open_flg !== undefined &&
              <OpenButton disabled={buttonStatus.open_flg} onClick={() => toggleButton(id, !versionState.open_flg)}>
                {versionState.open_flg ? 'Unpublish' : 'Publish'}
              </OpenButton>
            }
          </Div>
          {Object.keys(fileList).length !== 0 &&
          <ItemDetail 
            title='Detail information' 
            labels={{
              filename: 'File name', 
              version: 'Version', 
              updated_at: 'Latest update',
              open_flg: 'Published', 
              update_info: 'Update info'}} 
            items={{
              filename: fileList[id].key.split('_')[0],
              version: fileList[id].key.split('_')[1],
              updated_at: 
              <>
                <span style={{marginRight: 20}}>
                  {`File1: ${convertTimestampToDate(fileList[id].lastModified)}`}
                </span>
                File2: {fileList[id + '_2'] !== undefined ? <span>
                  {convertTimestampToDate(fileList[id + '_2'].lastModified)}
                </span> : <span>Not exist</span>}
              </>,
              open_flg: versionState.open_flg === undefined ? '' : openStatus[versionState.open_flg],
              update_info: versionState.update_info === undefined ? '' : nl2br(versionState.update_info)
            }}
            operation={{ 
              edit: { path: `/srv/version/edit/${fileList[id].key}` },
            }} 
          />}
        </div>
      </>
    );
  }
};
SrvVersionDetail.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
};
SrvVersionDetail.defaultProps = {
  match: {
    params: {
      id: '',
    }
  }
};
const Div = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;
const DeleteButton = styled(Button)`
  margin-right: 10px;
`;
const OpenButton = styled(Button)`
  margin-right: 10px;
`;
export default SrvVersionDetail;